import type { RestaurantRole } from '@/bindings/RestaurantRole'
import type { MessageKey } from '@/lib/i18n/messages/en'

/** Rank order, lowest first — mirrors the backend enum (`Staff < Admin < Manager < Superuser`). */
export const ROLES: readonly RestaurantRole[] = ['staff', 'admin', 'manager', 'superuser']
const RANK: Record<RestaurantRole, number> = { staff: 0, admin: 1, manager: 2, superuser: 3 }

export const ROLE_LABELS: Record<RestaurantRole, MessageKey> = {
  staff: 'role.staff',
  admin: 'role.admin',
  manager: 'role.manager',
  superuser: 'role.superuser',
}

export function isRole(value: unknown): value is RestaurantRole {
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value)
}

/** UI gate only — the backend's `RestaurantAccess` is the authority. */
export function canManageRestaurant(role: RestaurantRole): boolean {
  return RANK[role] >= RANK.manager
}

/** Branch management (`RestaurantAccess::can_manage_branch`): admin and above; the backend also checks scope. */
export function canManageBranch(role: RestaurantRole): boolean {
  return RANK[role] >= RANK.admin
}

/** Branch price override (`RestaurantAccess::can_override_price`, F4): manager and above. */
export function canOverridePrice(role: RestaurantRole): boolean {
  return RANK[role] >= RANK.manager
}

/** Strictly higher only (`RestaurantRole::can_manage`): the filter, grant, and revoke rule (E7). */
export function canManageRole(actor: RestaurantRole, target: RestaurantRole): boolean {
  return RANK[actor] > RANK[target]
}

/** The roles an actor may filter for or grant, lowest first. */
export function manageableRoles(actor: RestaurantRole): RestaurantRole[] {
  return ROLES.filter((role) => canManageRole(actor, role))
}

/** The accounts page's default filter (PRD): the rank just below the actor's. */
export function nextLowerRole(actor: RestaurantRole): RestaurantRole | undefined {
  return manageableRoles(actor).at(-1)
}

/** Staff/admin work in chosen branches; manager/superuser span the restaurant. */
export function isBranchScoped(role: RestaurantRole): boolean {
  return RANK[role] < RANK.manager
}
